import { DataGrid, PaginationParams, SelectionModel, SortModel } from '@if/mui'
import { FailedTransactionItem } from '@src/types/transaction-retry'
import { useMemo, useState } from 'react'
import { useTransactionDataGrid } from './TransactionRetry-hooks'

interface FailedTransactionsListProps {
  transactions: FailedTransactionItem[]
  selectedIds: Set<string>
  onSelectionChange: (ids: string[]) => void
  onViewReport: (fileName: string) => void
  onRetryProcessing: (fileName: string) => void
  onRetryMultiple: (fileNames: string[]) => void
  isLoading: boolean
}

interface TransactionRow {
  id: string
  fileName: string
  processDate: string | undefined
  errorCount: number
  transactionType: string
}

const DEFAULT_SORT: SortModel<TransactionRow> = { name: 'processDate', direction: 'desc' }

const FailedTransactionsList = ({
  transactions,
  selectedIds,
  onSelectionChange,
  onViewReport,
  onRetryProcessing,
  onRetryMultiple: _onRetryMultiple,
  isLoading,
}: FailedTransactionsListProps) => {
  const { columns, rows } = useTransactionDataGrid(transactions, onViewReport, onRetryProcessing)
  const [sortModel, setSortModel] = useState<SortModel<TransactionRow> | null>(DEFAULT_SORT)
  const [pagination, setPagination] = useState<PaginationParams>({ offset: 0, limit: 25 })

  const sortedRows = useMemo(() => {
    const ordered = [...rows]
    if (!sortModel) return ordered

    return ordered.sort((first, second) => {
      const left = first[sortModel.name] ?? ''
      const right = second[sortModel.name] ?? ''
      const comparison = left < right ? -1 : left > right ? 1 : 0
      return sortModel.direction === 'asc' ? comparison : -comparison
    })
  }, [rows, sortModel])

  const pageRows = useMemo(
    () => sortedRows.slice(pagination.offset, pagination.offset + pagination.limit),
    [sortedRows, pagination],
  )

  const selectionModel = useMemo<SelectionModel>(() => Array.from(selectedIds), [selectedIds])

  const gridProps = {
    columns,
    loading: isLoading,
    selectable: 'multi' as const,
    selectionModel,
    onSelectionModelChange: (model: SelectionModel) => onSelectionChange(model.map(String)),
    sortModel,
    onSortModelChange: setSortModel,
  }

  return (
    <DataGrid
      {...gridProps}
      rows={pageRows}
      pagination={pagination}
      onPaginationChange={setPagination}
      canChangePageSize
    />
  )
}

export default FailedTransactionsList
